import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useLanguage } from "../context/LanguageContext";
import { usePageMeta } from "../utils/usePageMeta";

const SafetyPage = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();

    // Legal pages exist in Ukrainian only, so they get a canonical but no hreflang pair
    usePageMeta({
        title: "Правила безпеки при роботі з інструментом — SmarTOOL",
        description: "Правила безпеки та експлуатації орендованого електроінструменту SmarTOOL.",
        canonical: "https://kh-tools.com/safety",
        lang: "uk",
    });

    return (
        <div className="min-h-screen bg-gray-50">
            <Header
                title={t("safety.title")}
                showBack
                onBack={() => navigate(-1)}
            />

            <div className="p-4 pb-8 animate-slide-in">
                <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
                    <div className="max-w-4xl mx-auto pt-0 p-6 bg-white shadow-lg rounded-lg my-8 text-gray-800">
                        <h1 className="text-3xl font-bold text-center text-gray-900 mb-6">
                            🛠️ ПРАВИЛА БЕЗПЕКИ ТА ЕКСПЛУАТАЦІЇ ІНСТРУМЕНТУ
                        </h1>

                        <p className="text-gray-600 mb-8 border-b pb-4">
                            Ці правила є невід'ємною частиною Договору оренди.
                            Орендар зобов'язується дотримуватися їх для
                            забезпечення власної безпеки та збереження майна
                            Орендодавця.
                        </p>

                        <div className="space-y-6">
                            {/* Раздел 1 */}
                            <section>
                                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                                    🛡️ 1. Загальні вимоги безпеки
                                </h2>
                                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
                                    <li>
                                        <strong>
                                            Засоби індивідуального захисту
                                            (ЗІЗ):
                                        </strong>{" "}
                                        Використовуйте захисні окуляри,
                                        рукавички, навушники та респіратори
                                        відповідно до типу робіт.
                                    </li>
                                    <li>
                                        <strong>Одяг:</strong> Не працюйте в
                                        одязі, що звисає, з розпущеним волоссям
                                        або прикрасами, які можуть потрапити в
                                        рухомі частини інструменту.
                                    </li>
                                    <li>
                                        <strong>Робоче місце:</strong>{" "}
                                        Підтримуйте порядок. Не працюйте у
                                        вологих приміщеннях або під дощем, якщо
                                        інструмент не має спеціального захисту
                                        (вологозахисту).
                                    </li>
                                    <li>
                                        <strong>Тверезість:</strong> Категорично
                                        заборонено використовувати інструмент у
                                        стані алкогольного, наркотичного або
                                        токсичного сп'яніння.
                                    </li>
                                </ul>
                            </section>

                            {/* Раздел 2 */}
                            <section>
                                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                                    ⚡ 2. Електроінструмент (дрилі, болгарки,
                                    лобзики, перфоратори)
                                </h2>
                                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
                                    <li>
                                        <strong>Перевірка:</strong> Перед
                                        увімкненням перевірте цілісність кабелю
                                        живлення та корпусу.
                                    </li>
                                    <li>
                                        <strong>Підключення:</strong>{" "}
                                        Переконайтеся, що напруга в мережі
                                        відповідає вимогам інструменту.
                                        Використовуйте справні подовжувачі.
                                    </li>
                                    <li>
                                        <strong>Заміна оснащення:</strong>{" "}
                                        Заміну свердел, дисків, пилочок
                                        проводьте <strong>тільки</strong> при
                                        відключеному від мережі інструменті.
                                    </li>
                                    <li>
                                        <strong>Фіксація:</strong> Надійно
                                        закріплюйте оброблювану деталь.
                                    </li>
                                    <li>
                                        <strong>Безпека:</strong> Не натискайте
                                        на інструмент із надмірною силою. Не
                                        торкайтеся рухомих частин до їх повної
                                        зупинки.
                                    </li>
                                </ul>
                            </section>

                            {/* Раздел 3 */}
                            <section>
                                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                                    🔋 3. Акумуляторний інструмент
                                </h2>
                                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
                                    <li>
                                        <strong>Акумулятори:</strong>{" "}
                                        Використовуйте тільки ті акумулятори та
                                        зарядні пристрої, що видані в комплекті.
                                    </li>
                                    <li>
                                        <strong>Температура:</strong> Не
                                        піддавайте акумулятори впливу прямих
                                        сонячних променів, вогню або температури
                                        вище +50°C.
                                    </li>
                                    <li>
                                        <strong>Волога:</strong> Бережіть
                                        акумулятори та інструмент від
                                        потрапляння води.
                                    </li>
                                </ul>
                            </section>

                            {/* Раздел 4 */}
                            <section>
                                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                                    ⛽ 4. Інструмент з ДВЗ (бензинові/дизельні
                                    генератори, двигуни)
                                </h2>
                                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
                                    <li>
                                        <strong>Паливо:</strong> Заправляйте
                                        інструмент тільки при заглушеному та
                                        холодному двигуні. Не паліть під час
                                        заправки.
                                    </li>
                                    <li>
                                        <strong>Вентиляція:</strong>{" "}
                                        Використовуйте інструмент з ДВЗ{" "}
                                        <strong>
                                            тільки на відкритому повітрі
                                        </strong>{" "}
                                        або в добре провітрюваних приміщеннях
                                        (небезпека отруєння чадним газом).
                                    </li>
                                    <li>
                                        <strong>Мастило:</strong> Перед
                                        використанням перевірте рівень мастила
                                        відповідно до інструкції.
                                    </li>
                                </ul>
                            </section>

                            {/* Раздел 5 */}
                            <section>
                                <h2 className="text-xl font-semibold text-gray-900 mb-3">
                                    🧹 5. Повернення інструменту
                                </h2>
                                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
                                    <li>
                                        <strong>Очищення:</strong> Повертайте
                                        інструмент очищеним від пилу, бруду,
                                        залишків будівельних сумішей.
                                    </li>
                                    <li>
                                        <strong>Комплектація:</strong>{" "}
                                        Повертайте інструмент у повній
                                        комплектації (кейс, акумулятори,
                                        зарядний пристрій, ключі, оснащення).
                                    </li>
                                </ul>
                            </section>
                        </div>

                        {/* Блок напоминания */}
                        <div className="mt-10 p-4 bg-gray-100 border-l-4 border-gray-500 rounded-r-lg">
                            <p className="text-gray-700">
                                <strong className="text-gray-900">
                                    📢 НАГАДУВАННЯ:
                                </strong>{" "}
                                Орендодавець{" "}
                                <strong>НЕ НЕСЕ відповідальності</strong> за
                                будь-яку шкоду (майнову, фізичну або моральну),
                                заподіяну Орендарю, третім особам або майну
                                третіх осіб у процесі використання або
                                транспортування Інструменту. Орендар приймає на
                                себе повний ризик та відповідальність за
                                наслідки використання Інструменту.
                            </p>
                        </div>

                        {/* Блок внимания */}
                        <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg">
                            <p className="text-yellow-800">
                                <strong className="text-yellow-900">
                                    ⚠️ УВАГА:
                                </strong>{" "}
                                Порушення цих правил свідчить про неправильне
                                використання інструменту, що тягне за собою
                                матеріальну відповідальність Орендаря за
                                пошкодження або втрату майна, а також
                                автоматичне списання коштів згідно з Договором
                                оренди.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SafetyPage;
